import { Component, h, ComponentInterface, Prop, Element, State } from '@stencil/core';

declare const assetPath;

@Component({
    tag: 'app-featured-media',
    styleUrl: 'app-featured-media.scss',
    shadow: false
})
export class AppFeaturedMedia implements ComponentInterface {

    @Element() root: HTMLElement;

    @Prop() heading: string;
    @Prop() description: string;
    @Prop() orientation: string = 'left';
    @Prop() imgSrc: string;
    @Prop() imgSrcWebp: string;
    @Prop() imgAlt: string;
    @Prop() hasVimeo: string;
    @Prop() vimeoUrl: string;
    @Prop() frameWidth: number;
    @Prop() frameHeight: number;

    @Prop() ctaPrimaryText: string;
    @Prop() ctaPrimaryHref: string;
    @Prop() ctaPrimaryClass: string;
    @Prop() ctaPrimaryTarget: string;
    @Prop() isCheckmark: string;

    @Prop({
        mutable: true,
        reflect: true
    }) ctaPrimaryRel: string;

    @Prop() ctaSecondaryText: string;
    @Prop() ctaSecondaryHref: string;
    @Prop() ctaSecondaryClass: string;
    @Prop() ctaSecondaryTarget: string;

    @Prop({
        mutable: true,
        reflect: true
    }) ctaSecondaryRel: string;

    @State() thumbnailUrl: string;

    componentWillLoad() {

        // set rel if component link targets are new window
        if ((this.ctaPrimaryClass && this.ctaPrimaryClass === 'speedbump-link') || (this.ctaPrimaryTarget && this.ctaPrimaryTarget.toLowerCase() === '_blank')) {
            this.ctaPrimaryRel = 'noopener';
        }

        if ((this.ctaSecondaryClass && this.ctaSecondaryClass === 'speedbump-link') || (this.ctaSecondaryTarget && this.ctaSecondaryTarget.toLowerCase() === '_blank')) {
            this.ctaSecondaryRel = 'noopener';
        }
        if (this.hasVimeo === '1') {
            this.fetchThumbnailUrl();
        }
    }

    private getClasses = () => {
        let classStr = 'app-featured-media';

        if (this.orientation.toLowerCase() === 'right') {
            return (`${classStr}__right`);
        }

        return classStr;
    }

    async fetchThumbnailUrl() {
        const apiUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(this.vimeoUrl)}`;

        try {
            const response = await fetch(apiUrl);

            if (response.ok) {
                const data = await response.json();
                this.thumbnailUrl = data.thumbnail_url.replace(/_\d+x\d+$/, '') || `${assetPath}/images/logo.png`;
            } else {
                console.error('Failed to fetch Vimeo oEmbed data:', response.status, response.statusText);
                this.thumbnailUrl = `${assetPath}/images/logo.png`
            }
        } catch (error) {
            console.error('Error fetching Vimeo oEmbed data:', error);
            `${assetPath}/images/logo.png`
        }
    }

    render() {
        return (
            <article class={`${this.getClasses()}`} aria-label="Featured media">
                <div class="container">
                    <div class="row content-wrapper">

                        {this.hasVimeo && this.hasVimeo === '1' ?
                            <div class="col-12 col-xl-6 cta-media">
                                <app-video-embed
                                    class="comp-load-defer"
                                    modal-id={`video-lightbox`}
                                    img-alt="placeholder video thumbnail"
                                    frame-width={this.frameWidth}
                                    frame-height={this.frameHeight}
                                    img-src={this.thumbnailUrl}
                                >
                                    <slot />
                                </app-video-embed>
                            </div> :
                            this.imgSrc &&
                            <div class="col-12 col-xl-6 cta-media">
                                <div class="img lazy-load-bg" style={this.getImgStyles()} role="presentation"></div>
                            </div>
                        }



                        <div class="col-12 col-xl-6 cta-content">
                            <div class="cta-content-inner">
                                <h2 class="heading">{this.heading}</h2>
                                {this.description && <div class={`description ${this.isCheckmark}`} innerHTML={this.description}></div>}

                                {(this.ctaPrimaryHref || this.ctaSecondaryHref) && (
                                    <div class="ctas">
                                        {(this.ctaPrimaryHref) && (
                                            <a
                                                href={this.ctaPrimaryHref}
                                                class={`btn btn-primary${this.ctaPrimaryClass ? ' speedbump-link' : ''}`}
                                                target={this.ctaPrimaryTarget}
                                                rel={this.ctaPrimaryRel}>
                                                {this.ctaPrimaryText}
                                            </a>
                                        )}

                                        {(this.ctaSecondaryHref) && (
                                            <a
                                                href={this.ctaSecondaryHref}
                                                class={`arrow-link__underline${this.ctaSecondaryClass ? ' speedbump-link' : ''}`}
                                                aria-label={this.ctaSecondaryText}
                                                target={this.ctaSecondaryTarget}
                                                rel={this.ctaSecondaryRel}>
                                                {this.ctaSecondaryText} <svg>
                                                    <use href="/themes/custom/qsci/frontend/www/assets/icon/sheets/icons.svg#chevron-right"></use>
                                                </svg>
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            </article>
        );
    }

    

    getImgStyles() {
        const styles: any = {
            backgroundImage: `url('${this.imgSrc}')`,
            backgroundSize: 'cover', 
            maxHeight: '420px', 
            minHeight: '223px'
        };

        // Check for support for -webkit-image-set
        if (window.CSS && window.CSS.supports && window.CSS.supports('background-image', `-webkit-image-set(url("${this.imgSrcWebp}") 1x)`)) {
            styles.backgroundImage = `-webkit-image-set(url('${this.imgSrcWebp}') 1x)`;
        }

        return styles;
    }
}
